import { Icon, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Painel from "./Painel";
import { useMaterialUIController } from "context";
import { TEXTO_COLORIDO_ESCURO } from "assets/coresTema";

// Status (subiu/caiu) sempre com seta + texto, nunca só pela cor
// No escuro, os tons claros de texto colorido do tema (o verde e o vermelho "main" ficavam abaixo de 3:1 no cinza)
const COR_SUBIU = { light: "#006300", dark: TEXTO_COLORIDO_ESCURO.success };
const COR_CAIU = { light: "#d03b3b", dark: TEXTO_COLORIDO_ESCURO.error };

const comAlfa = (hex, alfa) => {
    const valor = hex.replace("#", "");
    const [r, g, b] = [0, 2, 4].map(i => parseInt(valor.substring(i, i + 2), 16));
    return `rgba(${r}, ${g}, ${b}, ${alfa})`;
};

/**
 * Indicador do topo da tela (número em destaque, sem gráfico).
 * variacao: { texto: "+8,2%", positiva: true } ou null quando não há comparação. positiva dá a cor (bom/ruim);
 * a seta segue {@code subiu} quando informado (ex.: quebra que subiu é ruim: seta para cima, em vermelho).
 * detalheFixo: o detalhe continua aparecendo junto com a variação.
 * compacto: ícone e rótulo em cima e o número embaixo, na largura toda (seis cards numa linha).
 * onClick: o card vira botão (ex.: "ver os casos" da contagem), com a dica em {@code dicaClique}.
 */
export default function KpiCard({ icone, cor, rotulo, valor, detalhe, variacao, rotuloVariacao, detalheFixo, compacto, onClick, dicaClique }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const modo = darkMode ? "dark" : "light";

    const bolinha = (tamanho, fonte) => (
        <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ width: tamanho, height: tamanho, minWidth: tamanho, borderRadius: "50%", background: comAlfa(cor, darkMode ? 0.22 : 0.12) }}
        >
            <Icon sx={{ color: cor, fontSize: `${fonte}rem !important` }}>{icone}</Icon>
        </MDBox>
    );

    const resto = (
        <>
            {detalheFixo && variacao && detalhe && (
                <MDTypography variant="caption" color="text" sx={{ display: "block", opacity: 0.8 }}>
                    {detalhe}
                </MDTypography>
            )}
            {variacao ? (
                <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                    <Icon sx={{ color: (variacao.positiva ? COR_SUBIU : COR_CAIU)[modo], fontSize: "1rem !important" }}>
                        {(variacao.subiu ?? variacao.positiva) ? "arrow_upward" : "arrow_downward"}
                    </Icon>
                    <MDTypography variant="button" fontWeight="bold" sx={{ color: `${(variacao.positiva ? COR_SUBIU : COR_CAIU)[modo]} !important` }}>
                        {variacao.texto}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.8 }}>
                        {rotuloVariacao}
                    </MDTypography>
                </MDBox>
            ) : (
                detalhe && (
                    <MDTypography variant="caption" color="text" sx={{ display: "block", opacity: 0.8 }}>
                        {detalhe}
                    </MDTypography>
                )
            )}
        </>
    );

    // Card clicável: borda na cor do card ao passar o mouse ou com o foco do teclado
    const clicavel = (conteudo) => (onClick ? (
        <Tooltip title={dicaClique || "Ver os casos desta contagem"} placement="top">
            <MDBox
                role="button"
                tabIndex={0}
                onClick={onClick}
                onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
                sx={{
                    height: "100%",
                    cursor: "pointer",
                    borderRadius: 2,
                    outline: "none",
                    "& > .MuiCard-root": { transition: "border-color 0.15s" },
                    "&:hover > .MuiCard-root, &:focus-visible > .MuiCard-root": { borderColor: cor },
                }}
            >
                {conteudo}
            </MDBox>
        </Tooltip>
    ) : conteudo);

    if (compacto) {
        return clicavel(
            <Painel>
                <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                    {bolinha(36, 1.2)}
                    <MDTypography variant="button" color="text" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                        {rotulo}
                    </MDTypography>
                    {onClick && (
                        <Icon sx={{ ml: "auto", alignSelf: "flex-start", fontSize: "1.05rem !important", color: cor, opacity: 0.85 }}>list_alt</Icon>
                    )}
                </MDBox>
                <MDTypography variant="h5" fontWeight="bold" color="text" title={String(valor)}
                    sx={{ lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {valor}
                </MDTypography>
                {resto}
            </Painel>
        );
    }

    return clicavel(
        <Painel>
            <MDBox display="flex" alignItems="flex-start" gap={2}>
                {bolinha(52, 1.6)}
                <MDBox minWidth={0}>
                    <MDTypography variant="button" color="text" sx={{ display: "block", fontWeight: 500 }}>
                        {rotulo}
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="text" sx={{ lineHeight: 1.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {valor}
                    </MDTypography>
                    {resto}
                </MDBox>
            </MDBox>
        </Painel>
    );
}
